/**
 * Shared axios instance for the GlassPro API.
 *
 * All authenticated endpoints route through this instance so that:
 *   - Token attachment is centralized (request interceptor)
 *   - 401 → silent refresh → retry is centralized (response interceptor)
 *   - withCredentials and baseURL are configured once
 *
 * Two endpoints opt out of token attachment via `_skipAuthHeader: true`:
 *   - POST /v1/auth/login    (no token exists yet)
 *   - POST /v1/auth/refresh  (httpOnly cookie is the credential)
 *
 * The interceptors below import `getAccessToken` and `logout` from
 * `./auth`. This is intentional — the auth subsystem owns the token
 * lifecycle, and this file is its HTTP transport. The circular shape
 * (axios.ts → auth.ts → axios.ts) is broken at runtime by lazy reads:
 * the interceptor body runs on each request, by which time both modules
 * are fully initialized.
 *
 * See:
 *   - docs/adr/0009-storage-architecture-and-token-strategy.md
 *   - docs/api-contracts/auth.md
 */

import { getCookie } from '@/shared/lib/formatter'
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'

// Augment axios's per-request config with project-internal flags. Using
// module augmentation keeps these typed end-to-end without `as any` casts.
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    /** True when this request has already been retried after a 401. */
    _retry?: boolean
    /** True when this request must NOT have an Authorization header
     *  attached (login + refresh endpoints). */
    _skipAuthHeader?: boolean
  }
  interface AxiosRequestConfig {
    _retry?: boolean
    _skipAuthHeader?: boolean
  }
}

const { VITE_APP_BASE_URL } = import.meta.env

export const sharedAxios = axios.create({
  baseURL: VITE_APP_BASE_URL,
  withCredentials: true,
  timeout: 60_000,
})

// Request interceptor: attach Authorization + lang header.
sharedAxios.interceptors.request.use(async (config) => {
  // i18n header — sent on every request regardless of auth.
  config.headers['lang'] = getCookie('lang') ?? getCookie('i18next') ?? 'uzCYR'

  if (config._skipAuthHeader) {
    return config
  }

  // Lazy-import auth.ts to break the static circular dependency.
  // At runtime, by the time this interceptor fires, auth.ts is fully
  // initialized.
  //
  // getAccessToken is synchronous in Phase B (reads tokenMemory + cookie
  // fallback). It becomes async in Phase B+ when /v1/auth/refresh is
  // wired (an in-flight refresh Promise can be awaited). At that point
  // restore `await` on the call.
  const { getAccessToken } = await import('./auth')
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

// Response interceptor: 401 handling.
//
// Phase B (current): backend has no /v1/auth/refresh endpoint, so a 401
// means the token is invalid server-side (expired or revoked). There is
// no recovery path — force logout and bounce to /login.
//
// Phase B+ (when backend ships /v1/auth/refresh): the retry-with-refresh
// flow activates. The commented block below is the future implementation;
// the short-circuit logout afterwards becomes the fallback for when
// refresh itself fails.
sharedAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig | undefined
    const status = error.response?.status

    // Never retry the refresh or logout endpoints themselves — those paths
    // are terminal. Kept active as a guard: if Phase B+ is enabled, this
    // prevents refresh-on-refresh and logout-on-logout re-entry (a 401 from
    // the logout POST must not re-enter this branch and call logout() again);
    // if Phase B is current, both are no-ops because neither endpoint is
    // called yet.
    if (
      originalRequest?.url?.includes('/v1/auth/refresh') ||
      originalRequest?.url?.includes('/v1/auth/logout')
    ) {
      return Promise.reject(error)
    }

    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true

      // === PHASE B+ (uncomment when backend ships POST /v1/auth/refresh) ===
      // const { getAccessToken } = await import('./auth')
      // const newToken = await getAccessToken()
      // if (newToken) {
      //   originalRequest.headers = originalRequest.headers ?? {}
      //   originalRequest.headers.Authorization = `Bearer ${newToken}`
      //   return sharedAxios(originalRequest)
      // }

      // Phase B (current): token is invalid, no refresh available.
      // Programmatic clean-slate teardown — covers path-scoped cookie
      // residues that `logout()` alone cannot reach (see
      // src/shared/api/session-reset.ts for the H5 mechanism). The
      // utility is single-flight: N concurrent 401s collapse to one
      // teardown instead of cascading N parallel logouts.
      const { hardResetSession } = await import('./session-reset')
      await hardResetSession()
      // Idempotency guard: only redirect when not already on /login.
      // Without it, a 401 fired while on /login (e.g. an ungated protected
      // query) re-assigns the same href → full reload → loop. Mirrors the
      // guard in src/shared/storage/cross-tab.ts.
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        // Clean URL — no `?reason=session-expired` query. Avoids exposing
        // the reason for the bounce in the URL and keeps /login looking
        // like a fresh visit. Nothing in the UI reads the param.
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  },
)
